import React from "react";
import { UploadedFile } from "./types";
import useToastAlert from "@/hooks/useToastAlert";
import { fileIconDict, Severity } from "@velaro/velaro-component-lib";
import { getFileExtension } from "@/kb/helpers";

interface Props {
  files: UploadedFile[];
  onRemove(file: UploadedFile): void;
}

export default function UploadedFileEditor(props: Props) {
  const { displayToast } = useToastAlert();
  if (!props.files.length) {
    return null;
  }

  function renderFile(file: UploadedFile, index: number) {
    let imageSrc: string | null = null;

    const fileType = getFileExtension(
      file.fileName
    ) as keyof typeof fileIconDict;

    const FileIcon = fileIconDict[fileType] || fileIconDict["general"];

    if (isFileImage(fileType)) {
      imageSrc = file.url;
    }

    return (
      <div
        title={file.fileName}
        className="group border relative p-4 rounded w-36 h-40 flex flex-col gap-2 items-center justify-center bg-white"
        key={index}
      >
        {imageSrc && (
          <img
            alt={file.fileName}
            className="h-full min-h-0 object-scale-down flex-grow"
            src={imageSrc}
            onLoad={() => {
              URL.revokeObjectURL(imageSrc!);
            }}
          />
        )}
        {!imageSrc && <FileIcon className="h-full w-full text-slate-400" />}
        <div
          title={file.fileName}
          className="text-sm text-center w-full overflow-hidden whitespace-nowrap text-ellipsis text-slate-800 pb-1"
        >
          {file.fileName}
        </div>
        <div
          style={{ top: -10, left: -10 }}
          className="bg-white absolute border shadow rounded z-10 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto"
        >
          <button
            title={`Copy to clipboard`}
            className="w-8 h-8"
            onClick={() => {
              navigator.clipboard.writeText(file.url);
              displayToast(Severity.Info, "Copied to clipboard");
            }}
          >
            <i className="fa fa-light fa-copy"></i>
          </button>
        </div>
        <div
          style={{ top: -10, right: -10 }}
          className="bg-white absolute border shadow rounded z-10 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto"
        >
          <button
            title={`Remove ${file.fileName}`}
            className="text-crimson-red-600 w-8 h-8"
            onClick={() => {
              props.onRemove(file);
            }}
          >
            <i className="fa fa-times"></i>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap cursor-default pb-4 mb-4 gap-8 pt-3">
      {props.files.map(renderFile)}
    </div>
  );
}

function isFileImage(fileType: string | undefined) {
  return fileType === "jpg" || fileType === "jpeg" || fileType === "png";
}
